// Flagged Layouts — AI cabinet layouts the repair loop could NOT auto-complete.
//
// Backend: GET /api/admin/flagged-layouts (modules/admin_layout_review.js) — returns
//   { ok, count, items: [{ customerId, customerName, retailer, retailerSlug, room,
//                           attempts, remainingViolations:[{rule,severity,detail}],
//                           generatedAt }] }
//
// Purpose: surface the same signal the stress-test rooms gave (room + the rule
// violations that survived 3 repair attempts) where Steve actually works, so he can
// both clear the review backlog and spot generator patterns to feed back into the
// prompt/validator. Read-only. Cleanup aborts the in-flight fetch.

import { ApiError } from "@tkos/api-client";

import { registerPage } from "../../shell/router";
import type { Page, PageContext, PageCleanup } from "../../shell/types";

import "./styles.css";

// ── Types ────────────────────────────────────────────────────────────────────

interface FlaggedViolation {
  rule?: string;
  severity?: string;
  detail?: string;
}

interface FlaggedItem {
  customerId: string;
  customerName: string;
  retailer: string | null;
  retailerSlug: string | null;
  room: { widthMm?: number; lengthMm?: number } | null;
  attempts: number | null;
  remainingViolations: FlaggedViolation[];
  generatedAt: string | null;
}

interface FlaggedResponse {
  ok?: boolean;
  count?: number;
  items?: FlaggedItem[];
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

function fmtRoom(room: FlaggedItem["room"]): string {
  if (!room || !room.widthMm || !room.lengthMm) return "—";
  return `${(room.widthMm / 1000).toFixed(2)} × ${(room.lengthMm / 1000).toFixed(2)} m`;
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

// ── Page ───────────────────────────────────────────────────────────────────────

const flaggedLayoutsPage: Page = {
  name: "flagged-layouts",
  label: "Flagged Layouts",
  icon: "🚩",

  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    const root = document.createElement("section");
    root.className = "aflag";
    root.innerHTML = `
      <div class="aflag-head">
        <div>
          <h1>Flagged Layouts</h1>
          <p class="aflag-sub">AI cabinet layouts the generator couldn't auto-complete after 3 repair attempts. Room + the rule violations that survived — for clearing the backlog and spotting generator patterns.</p>
        </div>
        <button class="adm-btn adm-btn-ghost" data-refresh type="button">Refresh</button>
      </div>
      <div class="adm-card" data-body></div>
    `;
    container.appendChild(root);

    const bodyEl = root.querySelector<HTMLDivElement>("[data-body]")!;
    const refreshBtn = root.querySelector<HTMLButtonElement>("[data-refresh]")!;

    let ac = new AbortController();
    let loading = true;
    let error = "";
    let items: FlaggedItem[] = [];

    function rowHtml(it: FlaggedItem): string {
      const issues = (it.remainingViolations || []).length > 0
        ? it.remainingViolations
            .map((v) => `<span class="adm-pill adm-pill-warn" title="${esc(v.detail || "")}">${esc(v.rule || "?")}</span>`)
            .join(" ")
        : "—";
      return `
        <tr>
          <td>${esc(it.customerName)}</td>
          <td>${esc(it.retailer || "—")}</td>
          <td>${esc(fmtRoom(it.room))}</td>
          <td>${it.attempts == null ? "—" : String(it.attempts)}</td>
          <td class="aflag-issues">${issues}</td>
          <td>${esc(fmtDate(it.generatedAt))}</td>
        </tr>`;
    }

    function render(): void {
      if (loading) {
        bodyEl.innerHTML = `<div class="adm-loading">Loading…</div>`;
        return;
      }
      if (error) {
        bodyEl.innerHTML = `<div class="adm-error">${esc(error)}</div>`;
        return;
      }
      if (items.length === 0) {
        bodyEl.innerHTML = `<div class="adm-empty">No layouts awaiting review — every generated layout has passed.</div>`;
        return;
      }
      bodyEl.innerHTML = `
        <table class="adm-table aflag-table">
          <thead>
            <tr>
              <th>Customer</th><th>Retailer</th><th>Room</th>
              <th>Attempts</th><th>Issues</th><th>Generated</th>
            </tr>
          </thead>
          <tbody>${items.map(rowHtml).join("")}</tbody>
        </table>`;
    }

    async function refresh(): Promise<void> {
      loading = true;
      error = "";
      render();
      try {
        const resp = await ctx.api<FlaggedResponse>("/admin/flagged-layouts", { signal: ac.signal });
        items = Array.isArray(resp?.items) ? resp.items : [];
      } catch (err) {
        if (isAbortError(err)) return;
        error = err instanceof ApiError
          ? `Couldn't load flagged layouts (${err.status}).`
          : "Couldn't load flagged layouts.";
      }
      loading = false;
      render();
    }

    const onRefresh = (): void => {
      ac.abort();
      ac = new AbortController();
      void refresh();
    };
    refreshBtn.addEventListener("click", onRefresh);

    void refresh();

    return () => {
      ac.abort();
      refreshBtn.removeEventListener("click", onRefresh);
      root.remove();
    };
  },
};

registerPage(flaggedLayoutsPage);
export default flaggedLayoutsPage;
